'use client';
import React, { useEffect, useRef } from 'react';

export default function ProcessSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('[data-animate]');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" className="py-24 px-4 md:px-6 bg-paper" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        <div
          data-animate
          className="text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(28px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-ink-faint mb-4">How It Works</p>
          <h2
            className="font-sans font-extrabold text-ink leading-none"
            style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4.5rem)', letterSpacing: '-0.04em' }}
          >
            From Message to{' '}
            <span className="font-serif italic font-normal text-ink-muted">Fixed</span>,<br />
            in Three Steps.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Step 1 */}
          <div
            data-animate
            className="process-card relative"
            style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1) 0s, transform 0.6s cubic-bezier(0.16,1,0.3,1) 0s' }}
          >
            <span className="absolute top-5 right-6 font-extrabold text-ink/5 select-none" style={{ fontSize: '5rem', lineHeight: 1, letterSpacing: '-0.06em' }} aria-hidden="true">01</span>
            <div className="w-14 h-14 rounded-2xl border border-[rgba(13,13,13,0.1)] flex items-center justify-center text-ink mb-8">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-ink mb-3">Choose Your Service</h3>
            <p className="text-ink-muted text-base leading-relaxed">Select from HVAC, Locksmith, Roofing, or Plumbing. Each has a dedicated specialist team and direct phone line.</p>
            <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M12 5l5 5-5 5" stroke="rgba(13,13,13,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {/* Step 2 */}
          <div
            data-animate
            className="process-card relative"
            style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1) 0.12s, transform 0.6s cubic-bezier(0.16,1,0.3,1) 0.12s' }}
          >
            <span className="absolute top-5 right-6 font-extrabold text-ink/5 select-none" style={{ fontSize: '5rem', lineHeight: 1, letterSpacing: '-0.06em' }} aria-hidden="true">02</span>
            <div className="w-14 h-14 rounded-2xl border border-[rgba(13,13,13,0.1)] flex items-center justify-center text-ink mb-8">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-ink mb-3">Call the Specialist</h3>
            <p className="text-ink-muted text-base leading-relaxed">Tap the call button, describe the issue, and share your address. No forms, no hold music, no apps to download.</p>
            <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M12 5l5 5-5 5" stroke="rgba(13,13,13,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {/* Step 3 */}
          <div
            data-animate
            className="process-card relative"
            style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.6s cubic-bezier(0.16,1,0.3,1) 0.24s, transform 0.6s cubic-bezier(0.16,1,0.3,1) 0.24s' }}
          >
            <span className="absolute top-5 right-6 font-extrabold text-ink/5 select-none" style={{ fontSize: '5rem', lineHeight: 1, letterSpacing: '-0.06em' }} aria-hidden="true">03</span>
            <div className="w-14 h-14 rounded-2xl border border-[rgba(13,13,13,0.1)] flex items-center justify-center text-ink mb-8">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-ink mb-3">Pro Arrives &amp; Fixes It</h3>
            <p className="text-ink-muted text-base leading-relaxed">A licensed technician confirms your booking, arrives at the scheduled time, and completes the job with a written guarantee.</p>
          </div>
        </div>

        <p className="text-center text-ink-faint text-sm mt-10">
          No app to download. No account to create.{' '}
          <span className="font-semibold text-ink-muted">Just call us.</span>
        </p>
      </div>
    </section>
  );
}
